// HomeListingsStore.js - Dedicated store for PopularCategoryListingsComponent
import apiService from '@/services/apiService';

const state = () => ({
  allListings: [],
  categorizedListings: [],
  loading: false,
  error: null,
  lastFetch: 0,
  cacheValid: false,
  summary: {
    total_listings: 0,
    property_listings: 0,
    general_listings: 0,
    categories_count: 0,
  },
});

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes cache

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading;
  },

  SET_ALL_LISTINGS(state, listings) {
    state.allListings = listings || [];
  },

  SET_CATEGORIZED_LISTINGS(state, categorized) {
    state.categorizedListings = categorized || [];
  },

  SET_SUMMARY(state, summary) {
    state.summary = summary || {
      total_listings: 0,
      property_listings: 0,
      general_listings: 0,
      categories_count: 0,
    };
  },

  SET_ERROR(state, error) {
    state.error = error;
  },

  CLEAR_ERROR(state) {
    state.error = null;
  },

  SET_CACHE_VALID(state, isValid) {
    state.cacheValid = isValid;
  },

  SET_LAST_FETCH(state, timestamp) {
    state.lastFetch = timestamp;
  },

  RESET_STATE(state) {
    state.allListings = [];
    state.categorizedListings = [];
    state.loading = false;
    state.error = null;
    state.cacheValid = false;
    state.lastFetch = 0;
    state.summary = {
      total_listings: 0,
      property_listings: 0,
      general_listings: 0,
      categories_count: 0,
    };
  },
};

const actions = {
  /**
   * Initialize/fetch home listings with caching
   */
  async initialize({ commit, state }) {
    // Check cache validity
    const cacheAge = Date.now() - state.lastFetch;
    const cacheExpired = cacheAge > CACHE_TTL_MS;
    const needsInitialLoad = state.allListings.length === 0;

    // Use cache if valid and not expired
    if (!needsInitialLoad && !cacheExpired && state.cacheValid) {
      console.log('📋 HomeListingsStore: Using cached data');
      return;
    }

    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      console.log('📊 HomeListingsStore: Fetching fresh data from API');

      const response = await apiService.get('home-listings');

      // Extract data from response
      let responseData;
      if (response?.data?.original?.data) {
        responseData = response.data.original.data;
      } else if (response?.data?.data) {
        responseData = response.data.data;
      } else if (response?.data) {
        responseData = response.data;
      } else {
        throw new Error('Invalid API response structure');
      }

      // Update store with fetched data
      commit('SET_ALL_LISTINGS', responseData.all_listings || []);
      commit('SET_CATEGORIZED_LISTINGS', responseData.categorized_listings || []);
      commit('SET_SUMMARY', responseData.summary || {});
      commit('SET_LAST_FETCH', Date.now());
      commit('SET_CACHE_VALID', true);

      console.log('✅ HomeListingsStore: Successfully loaded listings', {
        total: responseData.summary?.total_listings || 0,
        property: responseData.summary?.property_listings || 0,
        general: responseData.summary?.general_listings || 0,
      });
    } catch (error) {
      console.error('❌ HomeListingsStore: Failed to fetch listings:', error);
      commit('SET_ERROR', error.message || 'Failed to fetch home listings');
      commit('SET_CACHE_VALID', false);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Force refresh data (ignore cache)
   */
  async refresh({ commit, dispatch }) {
    console.log('🔄 HomeListingsStore: Force refreshing data');
    commit('SET_CACHE_VALID', false);
    await dispatch('initialize');
  },

  /**
   * Clear all data
   */
  clearData({ commit }) {
    console.log('🗑️ HomeListingsStore: Clearing all data');
    commit('RESET_STATE');
  },
};

const getters = {
  // Basic getters
  allListings: (state) => state.allListings,
  categorizedListings: (state) => state.categorizedListings,
  isLoading: (state) => state.loading,
  hasError: (state) => !!state.error,
  error: (state) => state.error,
  summary: (state) => state.summary,

  // Utility getters
  hasData: (state) => state.allListings.length > 0,
  totalListings: (state) => state.allListings.length,

  // Category-specific getters
  getListingsByCategory: (state) => (categoryId) => {
    return state.allListings.filter((listing) => listing.category_id === categoryId);
  },

  getListingsByType: (state) => (type) => {
    return state.allListings.filter((listing) => listing.listing_type === type);
  },

  // Property listings only
  propertyListings: (state) => {
    return state.allListings.filter((listing) => listing.listing_type === 'property');
  },

  // General listings only
  generalListings: (state) => {
    return state.allListings.filter((listing) => listing.listing_type === 'general');
  },

  // Get popular categories with listing counts
  popularCategories: (state) => {
    return state.categorizedListings.map((category) => ({
      id: category.category_id,
      name: category.category_name,
      count: category.total_count,
      listings: category.listings.slice(0, 8), // Limit to 8 for display
    }));
  },

  // Check if data is fresh (for debugging)
  isCacheFresh: (state) => {
    const cacheAge = Date.now() - state.lastFetch;
    return cacheAge < CACHE_TTL_MS && state.cacheValid;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
